// ./src/utils/api/RecurringGiftService.ts
//
// ROADMAP #204 step 6a — the account's MONTHLY donors: each a gift to one cause charged every month (a Stripe
// subscription); every paid month is an ordinary gift (`DonationService`, filter `recurringGiftId`). The host lists them,
// stops one, and issues a past year's yearly summary receipts (they also go out on their own from January 15).

import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

/** incomplete (the first card payment not confirmed) | active | past_due (a charge failed — Stripe retries) | canceled */
export const RECURRING_STATUS_VALUES = ["incomplete", "active", "past_due", "canceled", "disconnected"] as const;

/** #288 — how many subscriptions still run, of each kind. */
export interface RecurringGiftRunning {
  total: number;
  gifts: number;
  memberships: number;
}

export interface RecurringGift {
  id?: string;
  accountId?: string;
  /** #204 step 6b — donation (a monthly gift) | membership (an auto-renewing membership). */
  lineKind?: string;
  causeId?: string | null;
  membershipTypeId?: string | null;
  organizerProfileId?: string | null;
  eventId?: string | null;
  personId?: string;
  buyerName?: string | null;
  buyerEmail?: string | null;
  currencyCode?: string;
  /** The gift each month. */
  amount?: number;
  /** What is charged each month (the gift + fees when the donor covers them). */
  chargeAmount?: number;
  billingInterval?: string;
  recurringStatus?: string;
  isTest?: boolean;
  wantsReceipt?: boolean;
  isAnonymous?: boolean;
  message?: string | null;
  startedAt?: string | null;
  /** The next charge. */
  currentPeriodEnd?: string | null;
  lastPaidAt?: string | null;
  canceledAt?: string | null;
  cancelReason?: string | null;
  /** donor | host | processor */
  canceledBy?: string | null;
  createdAt?: string;
  rights?: string[] | null;
}

export interface RecurringGiftFilter {
  id?: string[] | null;
  /** #204 step 6b — donation (monthly gifts) | membership (auto-renewing memberships). */
  lineKind?: string[] | null;
  causeId?: string[] | null;
  personId?: string[] | null;
  recurringStatus?: string[] | null;
  isTest?: boolean | null;
  /** The donor's name or email. */
  searchKeyword?: string | null;
}

/** What issuing a year's receipts did: how many, their numbers, and the donors it could not receipt (and why). */
export interface RecurringGiftYearRun {
  year: number;
  issued: number;
  receiptNumbers: string[];
  skipped: Array<{ personId: string; reason: string }>;
}

const FIELDS = `
  id accountId lineKind causeId membershipTypeId organizerProfileId eventId personId buyerName buyerEmail currencyCode amount chargeAmount
  billingInterval recurringStatus isTest wantsReceipt isAnonymous message startedAt currentPeriodEnd lastPaidAt
  canceledAt cancelReason canceledBy createdAt rights
`;

const LIST_QUERY = gql`
  query RecurringGiftList($filter: RecurringGiftFilter, $params: PaginationAndSorting) {
    recurringGiftList(filter: $filter, params: $params) {
      code
      total
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_QUERY = gql`
  query RecurringGiftGet($id: ID) {
    recurringGiftGet(id: $id) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RUNNING_QUERY = gql`
  query RecurringGiftRunning($params: RecurringGiftRunningInput) {
    recurringGiftRunning(params: $params) {
      code
      data { total gifts memberships }
      errors { name errors warnings }
    }
  }
`;

const STOP_ALL_MUTATION = gql`
  mutation RecurringGiftStopAllStart($params: RecurringGiftRunningInput) {
    recurringGiftStopAllStart(params: $params) {
      code
      data { id kind jobState totalRows processedRows lastError createdAt }
      errors { name errors warnings }
    }
  }
`;

const CANCEL_MUTATION = gql`
  mutation RecurringGiftCancel($params: RecurringGiftCancelInput) {
    recurringGiftCancel(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const ISSUE_YEAR_MUTATION = gql`
  mutation RecurringGiftIssueYearReceipts($params: RecurringGiftIssueYearInput) {
    recurringGiftIssueYearReceipts(params: $params) {
      code
      data { year issued receiptNumbers skipped { personId reason } }
      errors { name errors warnings }
    }
  }
`;

export class RecurringGiftService {
  static async list(filter?: RecurringGiftFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, { filter: filter || {}, params: { pagination: pagination || undefined, sortBy: sortBy || undefined } });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id }, { fetchPolicy: "network-only" });
  }

  /** #288 — how many subscriptions still run on the account, or on one organizer page. Always fresh. */
  static async running(organizerProfileId?: string | null): Promise<OpResult> {
    return ApiService.executeQuery(RUNNING_QUERY, { params: { organizerProfileId: organizerProfileId ?? null } }, { fetchPolicy: "network-only" });
  }

  /** #288 — stop them all, in the background. Answers the job to watch; none when nothing runs. */
  static async stopAllStart(organizerProfileId?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(STOP_ALL_MUTATION, { params: { organizerProfileId: organizerProfileId ?? null } });
  }

  /** Stop a monthly gift now (at Stripe); the donor is emailed. */
  static async cancel(id: string, reason?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(CANCEL_MUTATION, { params: { id, reason: reason ?? null } });
  }

  /** Issue a PAST year's yearly receipts now. */
  static async issueYearReceipts(year: number): Promise<OpResult> {
    return ApiService.executeMutation(ISSUE_YEAR_MUTATION, { params: { year } });
  }
}
